import React, { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import Breadcrumb from './Breadcrumb';
import ThemeToggle from './ThemeToggle';
import LanguageSwitcher from './LanguageSwitcher';
import CompanySwitcher from './CompanySwitcher';
import { ROUTES } from '@/lib/routes';
import type { CategoryKey } from '@/lib/navigation';

interface ResponsiveLayoutProps {
  children: ReactNode;
  title?: string;
  subtitle?: string;
  showBack?: boolean;
  /**
   * When set, back button will:
   * 1) remember this category in localStorage as `selectedCategory`
   * 2) navigate to Dashboard root
   *
   * Use this for Dashboard-based module categories like:
   * 'order' | 'product' | 'purchase' | 'expiry' | 'promotion' | 'analytics' | 'settings'
   */
  backToCategory?: CategoryKey;
  /**
   * When set, back button will always navigate to this route.
   * Takes precedence over `backToCategory`.
   *
   * Use this for section root pages that have their own URL,
   * e.g. `/product-overview`.
   */
  backToRoute?: string;
  showBreadcrumb?: boolean;
  showCompanySwitcher?: boolean;
  headerActions?: ReactNode;
  className?: string;
}

const ResponsiveLayout: React.FC<ResponsiveLayoutProps> = ({
  children,
  title,
  subtitle,
  showBack = false,
  backToCategory,
  backToRoute,
  showBreadcrumb = true,
  showCompanySwitcher = false,
  headerActions,
  className = ''
}) => {
  const navigate = useNavigate();

  return (
    <div className={`min-h-screen bg-background text-foreground ${className}`}>
      {/* Header */}
      <div className="bg-card/50 border-b border-border px-4 sm:px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4 min-w-0 flex-1">
            {showBack && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (backToRoute) {
                    navigate(backToRoute);
                    return;
                  }

                  if (backToCategory) {
                    // Persist the desired Dashboard category so the main menu
                    // highlights the correct section after navigation.
                    localStorage.setItem('selectedCategory', backToCategory);
                    navigate(ROUTES.dashboard.root);
                    return;
                  }

                  // Fallback: browser history back
                  navigate(-1);
                }}
                className="bg-card/50 border-border text-muted-foreground hover:bg-secondary/50 flex-shrink-0"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                返回
              </Button>
            )}
            
            <div className="min-w-0 flex-1">
              {showBreadcrumb && (
                <Breadcrumb className="mb-2" />
              )}
              
              {title && (
                <h1 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent truncate">
                  {title}
                </h1>
              )}
              
              {subtitle && (
                <p className="text-muted-foreground text-sm sm:text-base truncate">
                  {subtitle}
                </p>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            {showCompanySwitcher && (
              <div className="hidden sm:block">
                <CompanySwitcher showManagementButton={true} />
              </div>
            )}
            
            {headerActions}
            
            <div className="hidden sm:flex items-center gap-2">
              <ThemeToggle />
              <LanguageSwitcher />
            </div>
            
            {/* Mobile menu - could be implemented as a dropdown */}
            <div className="sm:hidden flex items-center gap-2">
              <ThemeToggle />
              <LanguageSwitcher />
            </div>
          </div>
        </div>
        
        {/* Mobile company switcher */}
        {showCompanySwitcher && (
          <div className="sm:hidden mt-3">
            <CompanySwitcher showManagementButton={true} className="w-full" />
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="flex-1">
        {children}
      </div>
    </div>
  );
};

export default ResponsiveLayout;
